import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import type { OcdTheme } from '../services/settingsTypes';

interface OcdThemeEditModalProps {
    theme: OcdTheme | null;
    isNew: boolean;
    onSave: (theme: OcdTheme, isNew: boolean) => Promise<void>;
    onClose: () => void;
}

export const OcdThemeEditModal = ({
    theme,
    isNew,
    onSave,
    onClose,
}: OcdThemeEditModalProps) => {
    const [key, setKey] = useState('');
    const [friendlyName, setFriendlyName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublished, setIsPublished] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (theme) {
            setKey(theme.key);
            setFriendlyName(theme.friendlyName);
            setDescription(theme.description);
            setIsPublished(theme.isPublished);
        } else {
            setKey('');
            setFriendlyName('');
            setDescription('');
            setIsPublished(true);
        }
    }, [theme]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!key.trim()) {
            setError('Key is required');
            return;
        }

        if (!friendlyName.trim()) {
            setError('Friendly name is required');
            return;
        }

        // Validate key format (lowercase letters, numbers, and hyphens only)
        if (!/^[a-z0-9-]+$/.test(key)) {
            setError('Key must contain only lowercase letters, numbers, and hyphens');
            return;
        }

        const themeData: OcdTheme = {
            key: key.trim(),
            friendlyName: friendlyName.trim(),
            description: description.trim(),
            isPublished,
            order: theme?.order || 0,
        };

        try {
            setIsSaving(true);
            await onSave(themeData, isNew);
        } catch (err) {
            console.error('Failed to save theme:', err);
            setError(err instanceof Error ? err.message : 'Failed to save theme');
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'Add New OCD Theme' : 'Edit OCD Theme'}
                    </h3>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-1">
                            Key *
                        </label>
                        <input
                            type="text"
                            id="key"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            disabled={!isNew}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., contamination"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {isNew
                                ? 'Lowercase letters, numbers, and hyphens only. Cannot be changed after creation.'
                                : 'Key cannot be changed after creation.'}
                        </p>
                    </div>

                    <div>
                        <label htmlFor="friendlyName" className="block text-sm font-medium text-gray-700 mb-1">
                            Friendly Name *
                        </label>
                        <input
                            type="text"
                            id="friendlyName"
                            value={friendlyName}
                            onChange={(e) => setFriendlyName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Contamination"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            The display name shown to users in the app.
                        </p>
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Optional description of this OCD theme"
                            rows={3}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Optional description for internal reference or display in the app.
                        </p>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isPublished"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
                            Published (visible to users)
                        </label>
                    </div>
                </form>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSaving}
                        disabled={isSaving}
                    >
                        {isNew ? 'Add Theme' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

